import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { Notification, useNotifications } from "@/hooks/useNotifications";
import { 
  Trophy, 
  Calendar, 
  UserPlus, 
  MessageSquare, 
  Target 
} from "lucide-react";

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'high_score_response':
      return <Trophy className="h-4 w-4 text-yellow-500" />;
    case 'interview_invitation':
      return <Calendar className="h-4 w-4 text-blue-500" />;
    case 'interview_availability_submitted':
      return <Calendar className="h-4 w-4 text-green-500" />;
    case 'collaboration_activity':
      return <UserPlus className="h-4 w-4 text-purple-500" />;
    case 'recruitment_status_changed':
      return <Target className="h-4 w-4 text-orange-500" />;
    default:
      return <MessageSquare className="h-4 w-4 text-muted-foreground" />;
  }
};

export function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();

  const handleClick = () => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  return (
    <div
      className={`p-3 rounded-md cursor-pointer transition-colors hover:bg-muted/50 ${
        !notification.is_read ? 'bg-primary/5 border-l-2 border-l-primary' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
              {notification.title}
            </h4>
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          {notification.action_label && notification.action_url && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-primary hover:no-underline"
            >
              {notification.action_label} →
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}